import { useState, type ComponentType, type SVGProps } from 'react';
import { BRAND } from '@/config/brand';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { updateTenantProfile } from '@/api/endpoints/tenant';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  User,
  Calendar,
  LayoutDashboard,
  Phone,
  Check,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface StepDef {
  id: 'welcome' | 'profile' | 'tour' | 'complete';
  title: string;
  subtitle: string;
  icon: IconType;
}

function getSteps(t: (key: string, options?: Record<string, unknown>) => string): StepDef[] {
  return [
    { id: 'welcome', title: t('admin:backoffice.onboarding.steps.welcome.title', { brand: BRAND.name }), subtitle: t('admin:backoffice.onboarding.steps.welcome.subtitle'), icon: Sparkles },
    { id: 'profile', title: t('admin:backoffice.onboarding.steps.profile.title'), subtitle: t('admin:backoffice.onboarding.steps.profile.subtitle'), icon: User },
    { id: 'tour', title: t('admin:backoffice.onboarding.steps.tour.title'), subtitle: t('admin:backoffice.onboarding.steps.tour.subtitle'), icon: LayoutDashboard },
    { id: 'complete', title: t('admin:backoffice.onboarding.steps.complete.title'), subtitle: t('admin:backoffice.onboarding.steps.complete.subtitle'), icon: Check },
  ];
}

interface TourFeature {
  icon: IconType;
  title: string;
  description: string;
}

function getTourFeatures(t: (key: string) => string): TourFeature[] {
  return [
    { icon: Phone, title: t('admin:backoffice.onboarding.features.calls.title'), description: t('admin:backoffice.onboarding.features.calls.description') },
    { icon: Calendar, title: t('admin:backoffice.onboarding.features.meetings.title'), description: t('admin:backoffice.onboarding.features.meetings.description') },
    { icon: TrendingUp, title: t('admin:backoffice.onboarding.features.performance.title'), description: t('admin:backoffice.onboarding.features.performance.description') },
    { icon: Target, title: t('admin:backoffice.onboarding.features.aiAssistant.title'), description: t('admin:backoffice.onboarding.features.aiAssistant.description') },
  ];
}

interface ClientLite {
  id?: string;
  contact_name?: string | null;
  company_name?: string | null;
  email?: string | null;
}

interface OnboardingWizardProps {
  client?: ClientLite | null;
  onComplete?: () => void;
  onUpdate?: () => void;
}

// DUP-1 (W6): no meeting-link step any more. customer.calendly_url is edited by
// the tenant admin in Paramètres › Intégrations, the only writer.
export default function OnboardingWizard({ client, onComplete, onUpdate }: OnboardingWizardProps) {
  const { t } = useTranslation(['admin']);
  const STEPS = getSteps(t);
  const TOUR_FEATURES = getTourFeatures(t);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    contact_name: client?.contact_name || '',
  });
  const [saving, setSaving] = useState(false);

  const progress = (currentStep / (STEPS.length - 1)) * 100;
  const step = STEPS[currentStep]!;

  const handleNext = async () => {
    if (currentStep === 1 && formData.contact_name && client?.id) {
      setSaving(true);
      await updateTenantProfile({ contact_name: formData.contact_name });
      onUpdate?.();
      setSaving(false);
    }

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete?.();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleSkip = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep(currentStep + 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
      >
        <div className="px-6 pt-6">
          <Progress value={progress} className="h-1" />
          <div className="flex justify-between mt-2 text-xs text-gray-400">
            <span>{t('admin:backoffice.onboarding.stepCounter', { current: currentStep + 1, total: STEPS.length })}</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{step.title}</h2>
                <p className="text-gray-500 text-sm mt-1">{step.subtitle}</p>
              </div>

              {step.id === 'welcome' && (
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-4">
                    {t('admin:backoffice.onboarding.welcomeIntro', { count: STEPS.length - 1 })}
                  </p>
                  <div className="flex justify-center gap-2">
                    {STEPS.slice(1, -1).map((s, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                        <s.icon className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-gray-700">{s.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {step.id === 'profile' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('admin:backoffice.onboarding.fullNameLabel')}</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder={t('admin:backoffice.onboarding.fullNamePlaceholder')}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('common:field.company')}</Label>
                    <Input value={client?.company_name || ''} disabled className="h-12 bg-gray-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('common:field.email')}</Label>
                    <Input value={client?.email || ''} disabled className="h-12 bg-gray-50" />
                  </div>
                </div>
              )}

              {step.id === 'tour' && (
                <div className="space-y-3">
                  {TOUR_FEATURES.map((feature, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl"
                    >
                      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 text-sm">{feature.title}</h4>
                        <p className="text-xs text-gray-500">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {step.id === 'complete' && (
                <div className="text-center py-4">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <p className="text-gray-600 mb-2">{t('admin:backoffice.onboarding.completeMessage')}</p>
                  <p className="text-sm text-gray-400">
                    {t('admin:backoffice.onboarding.completeHint')}
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="px-6 pb-6 flex items-center justify-between">
          <div>
            {currentStep > 0 && currentStep < STEPS.length - 1 && (
              <Button variant="ghost" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('admin:backoffice.onboarding.back')}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {currentStep > 0 && currentStep < STEPS.length - 1 && (
              <Button variant="ghost" onClick={handleSkip}>
                {t('admin:backoffice.onboarding.skip')}
              </Button>
            )}
            <Button onClick={handleNext} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving
                ? t('admin:backoffice.onboarding.saving')
                : currentStep === STEPS.length - 1
                  ? t('admin:backoffice.onboarding.start')
                  : (
                    <>
                      {currentStep === 0 ? t('admin:backoffice.onboarding.start') : t('admin:backoffice.onboarding.continue')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
